import { useMemo, useState, useEffect } from "react";
import { ChevronRight, ChevronLeft, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export interface CascadingCategory {
  id: string;
  name: string;
  parent_id: string | null;
  depth: number;
  display_order: number;
}

interface Props {
  categories: CascadingCategory[];
  value: string | null;
  onChange: (id: string | null) => void;
  placeholder?: string;
  className?: string;
}

export function CascadingCategoryPicker({
  categories,
  value,
  onChange,
  placeholder = "Select category...",
  className,
}: Props) {
  const [open, setOpen] = useState(false);
  const [currentParentId, setCurrentParentId] = useState<string | null>(null);

  const byId = useMemo(
    () => new Map(categories.map((c) => [c.id, c])),
    [categories]
  );

  const childrenOf = useMemo(() => {
    const map = new Map<string | null, CascadingCategory[]>();
    for (const c of categories) {
      const key = c.parent_id;
      const arr = map.get(key) ?? [];
      arr.push(c);
      map.set(key, arr);
    }
    for (const arr of map.values()) {
      arr.sort(
        (a, b) =>
          a.display_order - b.display_order || a.name.localeCompare(b.name)
      );
    }
    return map;
  }, [categories]);

  const hasChildren = (id: string) => (childrenOf.get(id)?.length ?? 0) > 0;

  const pathOf = (id: string | null): CascadingCategory[] => {
    const out: CascadingCategory[] = [];
    let cur = id ? byId.get(id) ?? null : null;
    while (cur) {
      out.unshift(cur);
      cur = cur.parent_id ? byId.get(cur.parent_id) ?? null : null;
    }
    return out;
  };

  const selectedPath = pathOf(value);
  const selectedLabel = selectedPath.map((c) => c.name).join(" > ");

  useEffect(() => {
    if (open) {
      if (value) {
        const node = byId.get(value);
        setCurrentParentId(node?.parent_id ?? null);
      } else {
        setCurrentParentId(null);
      }
    }
  }, [open, value, byId]);

  const currentParent = currentParentId ? byId.get(currentParentId) ?? null : null;
  const items = childrenOf.get(currentParentId) ?? [];

  const goBack = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!currentParent) return;
    setCurrentParentId(currentParent.parent_id);
  };

  const select = (id: string) => {
    onChange(id);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
            className
          )}
        >
          <span className={cn("truncate text-left", !selectedLabel && "text-muted-foreground")}>
            {selectedLabel || placeholder}
          </span>
          <span className="flex items-center gap-1 shrink-0 ml-2">
            {value && (
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    e.stopPropagation();
                    onChange(null);
                  }
                }}
                className="rounded p-0.5 hover:bg-muted text-muted-foreground hover:text-foreground"
                aria-label="Clear selection"
              >
                <X className="h-3.5 w-3.5" />
              </span>
            )}
            <ChevronRight className="h-4 w-4 text-muted-foreground rotate-90" />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] p-0 z-[9999] bg-white border-slate-200 text-slate-900"
        align="start"
        style={{ pointerEvents: 'auto' }}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200">
          {currentParent ? (
            <button
              type="button"
              onClick={goBack}
              className="flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
              Back
            </button>
          ) : (
            <span className="text-xs font-medium text-slate-600">Select category</span>
          )}
          {currentParent && (
            <span className="text-xs font-medium text-slate-700 truncate mx-2">
              {currentParent.name}
            </span>
          )}
          {value && (
            <button
              type="button"
              onClick={() => {
                onChange(null);
                setOpen(false);
              }}
              className="text-xs text-slate-500 hover:text-slate-900"
            >
              Clear
            </button>
          )}
        </div>

        <div className="max-h-[320px] overflow-y-auto py-1">
          {currentParent && (
            <button
              type="button"
              onClick={() => select(currentParent.id)}
              className="w-full text-left px-3 py-2 text-sm text-slate-800 hover:bg-slate-100 flex items-center justify-between"
            >
              <span>Use "{currentParent.name}"</span>
            </button>
          )}
          {items.length === 0 && !currentParent ? (
            <div className="px-3 py-6 text-center text-xs text-slate-500">
              No categories available.
            </div>
          ) : (
            items.map((c) => {
              const drillable = hasChildren(c.id);
              return (
                <div
                  key={c.id}
                  className="flex items-stretch hover:bg-slate-100"
                  onClick={() => select(c.id)}
                >
                  <button
                    type="button"
                    onClick={() => select(c.id)}
                    className="flex-1 text-left px-3 py-2 text-sm text-slate-800"
                  >
                    {c.name}
                  </button>
                  {drillable && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setCurrentParentId(c.id);
                      }}
                      className="px-2 flex items-center text-slate-500 hover:text-slate-900 border-l border-slate-200"
                      aria-label={`Drill into ${c.name}`}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  )}
                </div>
              );
            })
          )}
        </div>

        {!currentParent && (
          <div className="px-3 py-2 text-[11px] text-slate-500 border-t border-slate-200">
            Select at this level or drill down for more specific categories.
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

export default CascadingCategoryPicker;
